import { useCallback, useEffect, useRef, useState } from "react";

import {
  acceptHandoff,
  buildPopupUrl,
  newNonce,
  type HandoffSession,
} from "./extSignInHandoff";
import type { AuthProviderId } from "./authProviders";
import type { B2cTokens } from "./b2cClient";

const POPUP_FEATURES = "popup=yes,width=480,height=720";
const POLL_MS = 500;

/**
 * Iframe side of the sign-in hand-off. `start()` MUST be called from a click
 * handler (user activation) or the browser blocks the popup.
 *
 * The popup is deliberately opened WITHOUT `noopener`: we need `window.opener`
 * to be this window so the popup can post the tokens back.
 */
export function useExtSignInPopup(
  onSession: (providerId: AuthProviderId, tokens: B2cTokens) => void,
) {
  const [pending, setPending] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const nonceRef = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onSessionRef = useRef(onSession);
  onSessionRef.current = onSession;

  const reset = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    popupRef.current = null;
    nonceRef.current = null;
    setPending(false);
  }, []);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const nonce = nonceRef.current;
      const popup = popupRef.current;
      if (!nonce || !popup) return;
      const session: HandoffSession | null = acceptHandoff(event, {
        origin: window.location.origin,
        source: popup,
        nonce,
      });
      if (!session) return;
      // One-time nonce: consume before applying so a replay is ignored.
      reset();
      // Do NOT close the popup here: it closes itself after a short delay so
      // the browser's save-password prompt (credentials.store, which runs
      // after the popup posts) isn't torn down with the window.
      onSessionRef.current(session.providerId, session.tokens);
    }
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      // Unmounted mid-flow: don't leave an orphan sign-in window behind.
      const popup = popupRef.current;
      if (pollRef.current) clearInterval(pollRef.current);
      if (popup) {
        try {
          popup.close();
        } catch {
          /* ignore */
        }
      }
    };
  }, [reset]);

  const start = useCallback(() => {
    setBlocked(false);
    const nonce = newNonce();
    const popup = window.open(
      buildPopupUrl(window.location.origin, nonce),
      "m360-ext-signin",
      POPUP_FEATURES,
    );
    if (!popup) {
      setBlocked(true);
      return;
    }
    popupRef.current = popup;
    nonceRef.current = nonce;
    setPending(true);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      if (popupRef.current?.closed) reset();
    }, POLL_MS);
  }, [reset]);

  return { pending, blocked, start };
}
